"use client";

import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/utils/supabase/client";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AppointmentForm } from "@/components/AppointmentForm";
import { Badge } from "@/components/ui/badge";
import { Clock, MapPin } from "lucide-react";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { showSuccess, showError } from "@/utils/toast";

const CalendarPage = () => {
  const { profile, company } = useAuth();
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [editingAppointment, setEditingAppointment] = useState<any>(null);
  const [showForm, setShowForm] = useState(false);

  const { data: appointments } = useQuery({
    queryKey: ["appointments", company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      const { data } = await supabase
        .from("appointments")
        .select("*")
        .eq("company_id", company.id)
        .order("start_time");
      return data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (appointmentData: any) => {
      const { data, error } = await supabase
        .from("appointments")
        .insert({ ...appointmentData, company_id: company?.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      showSuccess("Appointment created!");
      setShowForm(false);
      queryClient.invalidateQueries({ queryKey: ["appointments", company?.id] });
    },
    onError: (error) => showError("Failed to create appointment"),
  });

  const updateMutation = useMutation({
    mutationFn: async (appointmentData: any) => {
      const { data, error } = await supabase
        .from("appointments")
        .update(appointmentData)
        .eq("id", appointmentData.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      showSuccess("Appointment updated!");
      setEditingAppointment(null);
      setShowForm(false);
      queryClient.invalidateQueries({ queryKey: ["appointments", company?.id] });
    },
    onError: (error) => showError("Failed to update appointment"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("appointments").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => showSuccess("Appointment deleted!"),
    onError: (error) => showError("Failed to delete appointment"),
  });

  const dayAppointments = appointments?.filter((appt: any) =>
    format(new Date(appt.start_time), "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd")
  ) || [];

  const handleSubmit = (data: any) => {
    const appointmentData = {
      ...data,
      start_time: data.start_time.toISOString(),
      end_time: data.end_time.toISOString(),
      ...(editingAppointment && { id: editingAppointment.id }),
    };
    if (editingAppointment) {
      updateMutation.mutate(appointmentData);
    } else {
      createMutation.mutate(appointmentData);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Calendar</h1>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild>
            <Button>
              <Clock className="mr-2 h-4 w-4" />
              New Appointment
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingAppointment ? "Edit Appointment" : "New Appointment"}</DialogTitle>
            </DialogHeader>
            <AppointmentForm
              initialData={editingAppointment}
              onSubmit={handleSubmit}
            />
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Schedule</CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(day) => day && setSelectedDate(day as Date)}
              className="rounded-md border"
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Today's Appointments ({format(selectedDate, "PPP")})</CardTitle>
          </CardHeader>
          <CardContent>
            {dayAppointments.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No appointments today.</p>
            ) : (
              dayAppointments.map((appt: any) => (
                <div key={appt.id} className="flex items-center space-x-3 p-3 border rounded-lg mb-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1">
                    <h4 className="font-medium">{appt.title}</h4>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(appt.start_time), "HH:mm")} - {format(new Date(appt.end_time), "HH:mm")}
                    </p>
                    {appt.location && (
                      <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        <span>{appt.location}</span>
                      </div>
                    )}
                  </div>
                  <Badge variant={appt.status === "confirmed" ? "default" : "secondary"}>{appt.status}</Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingAppointment(appt);
                      setShowForm(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteMutation.mutate(appt.id)}
                  >
                    Delete
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CalendarPage;